import { findElement, getColorAlphaValue } from './Utils/DomUtils';

type FreezedContainer = {
    vertical: Record<string, any>;
    horizontal: Record<string, any>;
};

type ItemState = {
    top: number;
    left: number;
    zIndex: string | number;
    hasBgColor: boolean;
};

export class UIFreezeCoordinator {
    private scaleFactor: number = 0;
    private placeholder: HTMLElement | null = null;
    private scrollableContainer: HTMLElement | null = null;
    private itemsInitialState: Record<string, Record<string, ItemState>> = {}; // Holds all items initial position per container
    private xFrozenAreasBounds: Record<string, number> = {}; //Holds the bounds of the frozen areas by X per container
    private yFrozenAreasBounds: Record<string, number> = {}; //Holds the bounds of the frozen areas by Y per container
    private freezeMaxZIndex: Record<string, number> = {};
    private freezeBGColor: Record<string, string> = {}; // Holds default background-color value per container
    private currentlyFrozenContainer: FreezedContainer = { vertical: {}, horizontal: {} }; //Holds whether freezing has been applied per container.
    private zIndex: number = 1;

    init(placeholder: HTMLElement): void {
        this.reset(placeholder);

        this.attachToScrollEvent();
    }

    reset(placeholder: HTMLElement): void {
        this.placeholder = placeholder;
        this.scrollableContainer = findElement(placeholder, '.trv-page-container');

        this.itemsInitialState = {};
        this.xFrozenAreasBounds = {};
        this.yFrozenAreasBounds = {};
        this.currentlyFrozenContainer = { vertical: {}, horizontal: {} };
    }

    setScaleFactor(scaleFactor: number): void {
        this.scaleFactor = scaleFactor;
    }

    private attachToScrollEvent(): void {
        this.scrollableContainer?.addEventListener('scroll', () => {
            if (this.scrollableContainer) {
                let freezeItems = this.scrollableContainer.querySelectorAll(
                    'div[data-sticky-id]'
                ) as NodeListOf<HTMLElement>;

                if (freezeItems.length) {
                    const uniqueIDs = new Set(Array.from(freezeItems).map((item) => item.dataset['stickyId'] || ''));

                    let scrollableContainerScrollTop = this.scrollableContainer.scrollTop;
                    let scrollableContainerScrollLeft = this.scrollableContainer.scrollLeft;

                    uniqueIDs.forEach((freezeItemsContainerID) => {
                        if (!this.itemsInitialState[freezeItemsContainerID]) {
                            this.saveFreezeItemsInitialState(freezeItemsContainerID);
                        }

                        this.updateFreezeItemsOnScroll(
                            freezeItemsContainerID,
                            scrollableContainerScrollTop,
                            scrollableContainerScrollLeft
                        );
                    });
                }
            }
        });
    }

    private saveFreezeItemsInitialState(freezeItemsContainerID: string) {
        let allFreezeItems = this.placeholder?.querySelectorAll(
            '[data-sticky-direction][data-sticky-id=\'' + freezeItemsContainerID + '\']'
        ) as NodeListOf<HTMLElement>;
        let freezeActions = this.placeholder?.querySelectorAll(
            '[data-reporting-action][data-sticky-id=\'' + freezeItemsContainerID + '\']'
        ) as NodeListOf<HTMLElement>;
        let itemMinLeft: number | null = null;
        let itemMinTop: number | null = null;
        let itemMaxRight: number | null = null;
        let itemMaxBottom: number | null = null;

        this.itemsInitialState[freezeItemsContainerID] = {};
        this.freezeBGColor[freezeItemsContainerID] =
            findElement(this.placeholder, '[data-id=\'' + freezeItemsContainerID + '\']')?.dataset['stickyBgColor'] || '';

        allFreezeItems.forEach((item) => {
            let scrollDirection = item.dataset['stickyDirection'];
            let itemID = item.dataset['id']?.toString() || '';
            let itemLeft = item.offsetLeft / this.scaleFactor;
            let itemRight = item.offsetLeft + item.offsetWidth * this.scaleFactor;
            let itemTop = item.offsetTop / this.scaleFactor;
            let itemBottom = item.offsetTop + item.offsetHeight * this.scaleFactor;

            let min = (base: number | null, arg: number) => (base === null || arg < base ? arg : base);
            let max = (base: number | null, arg: number) => (base === null || arg > base ? arg : base);

            switch (scrollDirection) {
                case 'Vertical':
                    itemMinTop = min(itemMinTop, itemTop);
                    itemMaxBottom = max(itemMaxBottom, itemBottom);
                    break;
                case 'Horizontal':
                    itemMinLeft = min(itemMinLeft, itemLeft);
                    itemMaxRight = max(itemMaxRight, itemRight);
                    break;
                default:
            }

            this._saveFreezeItemInitialState(freezeItemsContainerID, item, itemID);
        });

        this.freezeMaxZIndex[freezeItemsContainerID] = freezeActions?.length
            ? parseInt(getComputedStyle(freezeActions[0]).zIndex)
            : this.zIndex;
        this.yFrozenAreasBounds[freezeItemsContainerID] = (itemMaxBottom || 0) - (itemMinTop || 0);
        this.xFrozenAreasBounds[freezeItemsContainerID] = (itemMaxRight || 0) - (itemMinLeft || 0);
    }

    private _saveFreezeItemInitialState(freezeItemsContainerID: string, item: HTMLElement, itemID: string) {
        const itemComputedStyle = getComputedStyle(item);

        let hasInitialBgColor = this.hasSetBgColor(itemComputedStyle.backgroundColor);
        let zIndex: string | number = itemComputedStyle.zIndex;

        if (zIndex !== 'auto') {
            zIndex = parseFloat(zIndex);
        }

        let itemState: ItemState = {
            top: item.offsetTop,
            left: item.offsetLeft,
            zIndex: zIndex,
            hasBgColor: hasInitialBgColor
        };

        this.itemsInitialState[freezeItemsContainerID][itemID] = itemState;
    }

    private updateFreezeItemsOnScroll(
        freezeItemsContainerID: string,
        scrollableContainerScrollTop: number,
        scrollableContainerScrollLeft: number
    ) {
        let elementWrapper = findElement(this.placeholder, 'div[data-id=\'' + freezeItemsContainerID + '\']');

        if (!elementWrapper) {
            return;
        }

        let rowHeaders = this.placeholder?.querySelectorAll(
            '[data-sticky-direction*=\'Horizontal\'][data-sticky-id=\'' + freezeItemsContainerID + '\']'
        ) as NodeListOf<HTMLElement>;
        let colHeaders = this.placeholder?.querySelectorAll(
            '[data-sticky-direction*=\'Vertical\'][data-sticky-id=\'' + freezeItemsContainerID + '\']'
        ) as NodeListOf<HTMLElement>;

        if (this.isInScrollVisibleArea(elementWrapper)) {
            let pageContainer = elementWrapper.closest('.trv-report-page') as HTMLElement;
            let pageContainerComputedStyle = getComputedStyle(pageContainer);
            let pageContainerMargin = parseFloat(pageContainerComputedStyle.marginLeft);
            let pageContainerTopOffset = parseFloat(pageContainerComputedStyle.paddingTop);
            let pageContainerLeftOffset = parseFloat(pageContainerComputedStyle.paddingLeft);
            let pageContainerBorderTopWidth = parseFloat(pageContainerComputedStyle.borderTopWidth);
            let pageContainerBorderLeftWidth = parseFloat(pageContainerComputedStyle.borderLeftWidth);

            let hasFixRow = rowHeaders.length > 0;
            let hasFixColumn = colHeaders.length > 0;

            let elementWrapperTopPosition =
                elementWrapper.offsetTop +
                (pageContainer?.offsetTop || 0) +
                pageContainerMargin +
                pageContainerTopOffset +
                pageContainerBorderTopWidth;
            let elementWrapperLeftPosition =
                elementWrapper.offsetLeft +
                (pageContainer?.offsetLeft || 0) +
                pageContainerLeftOffset +
                pageContainerBorderLeftWidth;

            let verticalMoveOffset = scrollableContainerScrollTop - elementWrapperTopPosition;
            let horizontalMoveOffset = scrollableContainerScrollLeft - elementWrapperLeftPosition;

            if (hasFixColumn && verticalMoveOffset > 0) {
                //If reach the table end, stop freezing
                if (
                    scrollableContainerScrollTop <=
                    elementWrapper.offsetHeight * this.scaleFactor +
                        elementWrapperTopPosition -
                        this.yFrozenAreasBounds[freezeItemsContainerID]
                ) {
                    this.currentlyFrozenContainer.vertical[freezeItemsContainerID] = true;
                    this.updateUIElementsPosition(
                        colHeaders,
                        'top',
                        verticalMoveOffset / this.scaleFactor,
                        freezeItemsContainerID
                    );
                }
            } else {
                if (this.currentlyFrozenContainer.vertical[freezeItemsContainerID]) {
                    delete this.currentlyFrozenContainer.vertical[freezeItemsContainerID];
                    this.updateUIElementsPosition(colHeaders, 'top', -1, freezeItemsContainerID);
                }
            }

            if (hasFixRow && horizontalMoveOffset > 0) {
                if (
                    scrollableContainerScrollLeft <=
                    elementWrapper.offsetWidth * this.scaleFactor +
                        elementWrapperLeftPosition -
                        this.xFrozenAreasBounds[freezeItemsContainerID]
                ) {
                    this.currentlyFrozenContainer.horizontal[freezeItemsContainerID] = true;
                    this.updateUIElementsPosition(
                        rowHeaders,
                        'left',
                        horizontalMoveOffset / this.scaleFactor,
                        freezeItemsContainerID
                    );
                }
            } else {
                if (this.currentlyFrozenContainer.horizontal[freezeItemsContainerID]) {
                    delete this.currentlyFrozenContainer.horizontal[freezeItemsContainerID];
                    this.updateUIElementsPosition(rowHeaders, 'left', -1, freezeItemsContainerID);
                }
            }
        } else {
            if (
                this.currentlyFrozenContainer.horizontal[freezeItemsContainerID] ||
                this.currentlyFrozenContainer.vertical[freezeItemsContainerID]
            ) {
                this.resetToDefaultPosition(freezeItemsContainerID, rowHeaders, colHeaders);
            }
        }
    }

    private resetToDefaultPosition(
        freezeItemsContainerID: string,
        rowHeaders: NodeListOf<HTMLElement>,
        colHeaders: NodeListOf<HTMLElement>
    ) {
        //Reset items position, by passing offset parameter of updateUIElementsPosition as a negative number
        this.updateUIElementsPosition(colHeaders, 'top', -1, freezeItemsContainerID);
        this.updateUIElementsPosition(rowHeaders, 'left', -1, freezeItemsContainerID);

        delete this.currentlyFrozenContainer.horizontal[freezeItemsContainerID];
        delete this.currentlyFrozenContainer.vertical[freezeItemsContainerID];
    }

    private updateUIElementsPosition(
        targetElements: NodeListOf<HTMLElement>,
        position: string,
        offset: number,
        freezeItemsContainerID: string
    ) {
        targetElements.forEach((element) => {
            let itemFreezeDirection = element.dataset['stickyDirection'] || '';
            let isFrozenBothDirection = itemFreezeDirection.indexOf(',') > 0;
            let itemID = element.dataset['id'] || '';
            let itemInitialState = this.itemsInitialState[freezeItemsContainerID][itemID];
            let itemNewPostion = position == 'top' ? itemInitialState.top : itemInitialState.left;
            let initialZIndex = itemInitialState.zIndex;
            let hasInitialBgColor = itemInitialState.hasBgColor;
            let zIndexValue: string | number = 1;
            let maxZIndex = this.freezeMaxZIndex[freezeItemsContainerID]
                ? this.freezeMaxZIndex[freezeItemsContainerID]
                : zIndexValue;

            if (isFrozenBothDirection) {
                zIndexValue = initialZIndex !== 'auto' ? initialZIndex : maxZIndex + 2;
            } else {
                zIndexValue = initialZIndex !== 'auto' ? (initialZIndex as number) + 1 : maxZIndex;
            }

            let newStyleRules: Record<string, any> = {
                zIndex: zIndexValue
            };

            if (offset >= 0) {
                itemNewPostion = itemNewPostion + offset;
            } else {
                newStyleRules['zIndex'] = initialZIndex;
            }

            if (!hasInitialBgColor) {
                this.applyBgColorOnScroll(
                    element,
                    isFrozenBothDirection,
                    hasInitialBgColor,
                    offset >= 0,
                    freezeItemsContainerID
                );
            }

            newStyleRules[position] = itemNewPostion + 'px';

            Object.assign(element.style, newStyleRules);
        });
    }

    // eslint-disable-next-line max-params
    private applyBgColorOnScroll(
        item: HTMLElement,
        isItemFrozenBothDirection: boolean,
        hasInitialBgColor: boolean,
        shouldApplyBGColor: boolean,
        freezeItemsContainerID: string
    ) {
        if (item.tagName === 'IMG') {
            return;
        }

        if (isItemFrozenBothDirection && this.isFrozen(freezeItemsContainerID) && !hasInitialBgColor) {
            item.style.backgroundColor = this.freezeBGColor[freezeItemsContainerID];
            return;
        }

        if (shouldApplyBGColor) {
            item.style.backgroundColor = this.freezeBGColor[freezeItemsContainerID];
        } else {
            item.style.backgroundColor = 'initial';
        }
    }

    private hasSetBgColor(bgColorValue: string): boolean {
        return getColorAlphaValue(bgColorValue) > 0;
    }

    private isFrozen(freezeItemsContainerID: string): boolean {
        return (
            this.currentlyFrozenContainer.horizontal[freezeItemsContainerID] ||
            this.currentlyFrozenContainer.vertical[freezeItemsContainerID]
        );
    }

    private isInScrollVisibleArea(element: HTMLElement): boolean {
        if (!this.scrollableContainer) {
            return false;
        }

        const elementPosition = element.getBoundingClientRect();
        const parentPosition = this.scrollableContainer.getBoundingClientRect();

        return (
            this.isVisibleVertically(elementPosition, this.scrollableContainer, parentPosition) &&
            this.isVisibleHorizontally(elementPosition, this.scrollableContainer, parentPosition)
        );
    }

    private isVisibleVertically(elementPosition: DOMRect, parent: HTMLElement, parentPosition: DOMRect): boolean {
        let elementWidth = elementPosition.width;

        return (
            elementPosition.left > parent.scrollLeft - elementWidth &&
            elementPosition.left < parent.scrollLeft + elementWidth + parentPosition.width
        );
    }

    private isVisibleHorizontally(elementPosition: DOMRect, parent: HTMLElement, parentPosition: DOMRect): boolean {
        let elementHeight = elementPosition.height;

        return (
            elementPosition.top > parent.scrollTop - elementHeight &&
            elementPosition.top < parent.scrollTop + elementHeight + parentPosition.height
        );
    }
}
